import { Sport } from '#src/__generated__/globalTypes.ts';
import type { Color } from '#src/style/types.ts';

const LEVELS = {
  veryLow: 'veryLow',
  low: 'low',
  mediumLow: 'mediumLow',
  medium: 'medium',
  mediumHigh: 'mediumHigh',
  high: 'high',
  max: 'max',
} as const;
const THRESHOLD_COLORS: { [key in keyof typeof LEVELS]: Color } = {
  [LEVELS.veryLow]: 'var(--c-score-veryLow)',
  [LEVELS.low]: 'var(--c-score-low)',
  [LEVELS.mediumLow]: 'var(--c-score-mediumLow)',
  [LEVELS.medium]: 'var(--c-score-medium)',
  [LEVELS.mediumHigh]: 'var(--c-score-mediumHigh)',
  [LEVELS.high]: 'var(--c-score-high)',
  [LEVELS.max]: 'var(--c-gradient-score-max)',
} as const;

export type ScoreType =
  | 'CARD_SCORE'
  | 'PLAYER_SCORE'
  | 'TEAM_SCORE'
  | 'ALL_AROUND_SCORE';

const teamScoreThresholds = [
  [72.49, 'veryLow'],
  [147.49, 'low'],
  [222.49, 'mediumLow'],
  [197.49, 'medium'],
  [372.49, 'mediumHigh'],
] as const;

const allAroundScoreThresholds = [
  [0.01, 'veryLow'],
  [4.99, 'low'],
  [14.99, 'mediumLow'],
  [24.99, 'medium'],
  [39.99, 'mediumHigh'],
] as const;

export const thresholdsBySport = {
  [Sport.FOOTBALL]: [
    [20, 'veryLow'],
    [35, 'low'],
    [50, 'mediumLow'],
    [60, 'medium'],
    [75, 'mediumHigh'],
  ] as const,
  [Sport.NBA]: [
    [4.49, 'veryLow'],
    [14.49, 'low'],
    [24.49, 'mediumLow'],
    [34.49, 'medium'],
    [49.49, 'mediumHigh'],
  ] as const,
  [Sport.BASEBALL]: [
    [2.49, 'veryLow'],
    [7.49, 'low'],
    [11.49, 'mediumLow'],
    [19.49, 'medium'],
    [29.49, 'mediumHigh'],
  ] as const,
} as const;
export const maxScoresBySport = {
  [Sport.FOOTBALL]: 100,
  [Sport.NBA]: 75,
  [Sport.BASEBALL]: 45,
};

export type Props = { color: Color; level: keyof typeof LEVELS };
export const findThresholdColor = (
  score: number,
  sport: Sport,
  type?: ScoreType
): Props => {
  let level;

  if (sport === Sport.FOOTBALL && type === 'ALL_AROUND_SCORE') {
    level = allAroundScoreThresholds.find(t => Number(t[0]) > score)?.[1];
  } else if (sport === Sport.FOOTBALL && type === 'TEAM_SCORE') {
    level = teamScoreThresholds.find(t => Number(t[0]) >= score)?.[1];
  } else if (score >= maxScoresBySport[sport]) {
    level = LEVELS.max;
  } else {
    level = thresholdsBySport[sport].find(t => t[0] >= score)?.[1];
  }

  return {
    color: THRESHOLD_COLORS[level || LEVELS.high],
    level: level || LEVELS.high,
  };
};
